"use client";

import React from "react";
import { Loader as Loader2, CircleCheck as CheckCircle2 } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-primary/20 rounded-lg flex items-center justify-center mb-4">
          <CheckCircle2 className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground">
          Message Received
        </h3>
        <p className="text-muted-foreground max-w-md font-sans">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Full Name</FormLabel>
              <FormControl>
                <Input className="bg-input/50 border-border/50 focus-visible:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Work Email</FormLabel>
              <FormControl>
                <Input type="email" className="bg-input/50 border-border/50 focus-visible:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <FormField control={control} name="businessName" rules={{
        required: "Business name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Business Name</FormLabel>
              <FormControl>
                <Input className="bg-input/50 border-border/50 focus-visible:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="businessType" rules={{
        required: "Please select a business type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Business Type</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-input/50 border-border/50 focus-visible:ring-primary">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="homecare_agency">Homecare Agency</SelectItem>
                  <SelectItem value="senior_living">Senior Living</SelectItem>
                  <SelectItem value="community_health">Community Health</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 mb-6 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto min-w-[200px] bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-primary-foreground border-0 shadow-md" size="lg">
        {isSubmitting ? <>
            <Loader2 className="animate-spin mr-2 size-5" />
            Submitting...
          </> : "Get Started"}
      </Button>
    </Form>;
}
export default function BusinessContactForm() {
  return <section id="business-contact-form" className="dark bg-background text-foreground py-24 lg:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-lg bg-primary/5 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-lg bg-secondary/5 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold mb-4 tracking-tight">
              Let's scale your business together
            </h2>
            <p className="text-lg text-muted-foreground font-sans max-w-2xl mx-auto">
              Fill out the form below and our team will get back to you within 24 hours to discuss how we can help you achieve your goals.
            </p>
          </div>

          <Card className="bg-card/50 backdrop-blur-sm border-border/50 shadow-2xl">
            <CardHeader className="pb-8">
              <CardTitle className="text-2xl font-default">Contact Information</CardTitle>
              <CardDescription className="text-base">
                Please provide your details so we can route your inquiry to the right specialist.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FormProvider formId="business-contact-form" sectionName="Business Contact" defaultValues={{
              name: "",
              email: "",
              businessName: "",
              businessType: ""
            }} submitText="Get Started" successMessage="Thank you for reaching out! We have received your information and will be in touch shortly.">
                <ContactFormInner />
              </FormProvider>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}